// src/features/document/logic.ts
// Framework-agnostic business logic for Documents.
// Zero React imports — pure TypeScript functions and data maps.

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatDate(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '--';
  // Go zero time ('0001-01-01…') means the field was never set.
  if (new Date(dateStr).getFullYear() < 1971) return '--';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return String(dateStr);
  }
}

export function formatRelativeTime(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '--';
  // Go zero time ('0001-01-01…') means the field was never set.
  if (new Date(dateStr).getFullYear() < 1971) return '--';
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const sec = Math.floor(diff / 1000);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' });
    if (sec < 60) return rtf.format(-sec, 'second');
    const min = Math.floor(sec / 60);
    if (min < 60) return rtf.format(-min, 'minute');
    const hr = Math.floor(min / 60);
    if (hr < 24) return rtf.format(-hr, 'hour');
    const day = Math.floor(hr / 24);
    if (day < 7) return rtf.format(-day, 'day');
    if (day < 30) return rtf.format(-Math.floor(day / 7), 'week');
    return formatDate(dateStr, locale);
  } catch {
    return String(dateStr);
  }
}

// ---------------------------------------------------------------------------
// Entity helpers
// ---------------------------------------------------------------------------

export function getEntityId(item: any): string {
  return item?.entity?.id || item?.id || '';
}

export function getDocumentTitle(item: any): string {
  return item?.title || '';
}

export function getDocumentStatus(item: any): string {
  return item?.status || '';
}

export function getDocumentType(item: any): string {
  return item?.type || '';
}

export function getDocumentConfidentiality(item: any): string {
  return item?.confidentiality || '';
}

export function getDocumentOwner(item: any): string {
  return item?.owner?.display_name || item?.owner?.name || '';
}

export function getDocumentDepartment(item: any): string {
  return item?.department || '';
}

export function getDocumentTags(item: any): string[] {
  return item?.tags || [];
}

// ---------------------------------------------------------------------------
// Type → display maps (return i18n keys)
// ---------------------------------------------------------------------------

export type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'primary' | 'secondary';

interface StatusMapping {
  variant: BadgeVariant;
  labelKey: string;
}

export const documentStatusMap: Record<string, StatusMapping> = {
  DOCUMENT_STATUS_DRAFT: { variant: 'secondary', labelKey: 'document.status.draft' },
  DOCUMENT_STATUS_IN_REVIEW: { variant: 'warning', labelKey: 'document.status.inReview' },
  DOCUMENT_STATUS_PENDING_APPROVAL: { variant: 'warning', labelKey: 'document.status.pendingApproval' },
  DOCUMENT_STATUS_APPROVED: { variant: 'success', labelKey: 'document.status.approved' },
  DOCUMENT_STATUS_PUBLISHED: { variant: 'success', labelKey: 'document.status.published' },
  DOCUMENT_STATUS_REVISION: { variant: 'primary', labelKey: 'document.status.revision' },
  DOCUMENT_STATUS_OBSOLETE: { variant: 'secondary', labelKey: 'document.status.obsolete' },
  DOCUMENT_STATUS_ARCHIVED: { variant: 'secondary', labelKey: 'document.status.archived' },
  DOCUMENT_STATUS_WITHDRAWN: { variant: 'error', labelKey: 'document.status.withdrawn' },
};

export const documentTypeMap: Record<string, StatusMapping> = {
  DOCUMENT_TYPE_POLICY: { variant: 'primary', labelKey: 'document.docType.policy' },
  DOCUMENT_TYPE_MANUAL: { variant: 'primary', labelKey: 'document.docType.manual' },
  DOCUMENT_TYPE_PROCEDURE: { variant: 'default', labelKey: 'document.docType.procedure' },
  DOCUMENT_TYPE_WORK_INSTRUCTION: { variant: 'default', labelKey: 'document.docType.workInstruction' },
  DOCUMENT_TYPE_FORM: { variant: 'secondary', labelKey: 'document.docType.form' },
  DOCUMENT_TYPE_RECORD: { variant: 'secondary', labelKey: 'document.docType.record' },
  DOCUMENT_TYPE_SPECIFICATION: { variant: 'warning', labelKey: 'document.docType.specification' },
  DOCUMENT_TYPE_EXTERNAL: { variant: 'secondary', labelKey: 'document.docType.external' },
  DOCUMENT_TYPE_GUIDELINE: { variant: 'default', labelKey: 'document.docType.guideline' },
  DOCUMENT_TYPE_CONTRACT: { variant: 'error', labelKey: 'document.docType.contract' },
  DOCUMENT_TYPE_REPORT: { variant: 'warning', labelKey: 'document.docType.report' },
  DOCUMENT_TYPE_MEMO: { variant: 'secondary', labelKey: 'document.docType.memo' },
  DOCUMENT_TYPE_PLAN: { variant: 'primary', labelKey: 'document.docType.plan' },
  DOCUMENT_TYPE_DRAWING: { variant: 'default', labelKey: 'document.docType.drawing' },
  DOCUMENT_TYPE_LETTER: { variant: 'secondary', labelKey: 'document.docType.letter' },
  DOCUMENT_TYPE_OTHER: { variant: 'secondary', labelKey: 'document.docType.other' },
};

export const documentConfidentialityMap: Record<string, StatusMapping> = {
  DOCUMENT_CONFIDENTIALITY_PUBLIC: { variant: 'success', labelKey: 'document.confidentiality.public' },
  DOCUMENT_CONFIDENTIALITY_INTERNAL: { variant: 'default', labelKey: 'document.confidentiality.internal' },
  DOCUMENT_CONFIDENTIALITY_RESTRICTED: { variant: 'warning', labelKey: 'document.confidentiality.restricted' },
  DOCUMENT_CONFIDENTIALITY_CONFIDENTIAL: { variant: 'error', labelKey: 'document.confidentiality.confidential' },
  DOCUMENT_CONFIDENTIALITY_SECRET: { variant: 'error', labelKey: 'document.confidentiality.secret' },
};

export function getStatusMapping(map: Record<string, StatusMapping>, key: string | undefined): StatusMapping {
  if (!key) return { variant: 'secondary', labelKey: 'common.unknown' };
  return map[key] ?? { variant: 'secondary', labelKey: 'common.unknown' };
}

// ---------------------------------------------------------------------------
// Color swatches — document type accent color
// ---------------------------------------------------------------------------

export const documentTypeColorOptions = [
  { value: '#6366f1', label: 'Indigo' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#ef4444', label: 'Red' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#6b7280', label: 'Gray' },
] as const;

// ---------------------------------------------------------------------------
// Type icon paths (SVG stroke paths)
// ---------------------------------------------------------------------------

export const documentTypeIconMap: Record<string, string> = {
  DOCUMENT_TYPE_POLICY: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  DOCUMENT_TYPE_MANUAL: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  DOCUMENT_TYPE_PROCEDURE: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  DOCUMENT_TYPE_WORK_INSTRUCTION: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  DOCUMENT_TYPE_FORM: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  DOCUMENT_TYPE_RECORD: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4',
  DOCUMENT_TYPE_SPECIFICATION: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  DOCUMENT_TYPE_EXTERNAL: 'M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14',
  DOCUMENT_TYPE_GUIDELINE: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  DOCUMENT_TYPE_CONTRACT: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  DOCUMENT_TYPE_REPORT: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  DOCUMENT_TYPE_MEMO: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z',
  DOCUMENT_TYPE_PLAN: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  DOCUMENT_TYPE_DRAWING: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
  DOCUMENT_TYPE_LETTER: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  DOCUMENT_TYPE_OTHER: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z',
};

export function getTypeIconPath(type: string | undefined): string {
  if (!type) return documentTypeIconMap.DOCUMENT_TYPE_OTHER;
  return documentTypeIconMap[type] ?? documentTypeIconMap.DOCUMENT_TYPE_OTHER;
}

// ---------------------------------------------------------------------------
// Confidentiality icon paths
// ---------------------------------------------------------------------------

export function getConfidentialityIconPath(conf: string | undefined): string {
  if (!conf) return '';
  switch (conf) {
    case 'DOCUMENT_CONFIDENTIALITY_SECRET':
    case 'DOCUMENT_CONFIDENTIALITY_CONFIDENTIAL':
      return 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z';
    case 'DOCUMENT_CONFIDENTIALITY_RESTRICTED':
      return 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21';
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Avatar helpers
// ---------------------------------------------------------------------------

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name[0] || '?').toUpperCase();
}

export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const colors = ['#3B82F6', '#8B5CF6', '#EC4899', '#EF4444', '#F97316', '#22C55E', '#14B8A6', '#6366F1'];
  return colors[Math.abs(hash) % colors.length];
}

// ---------------------------------------------------------------------------
// Filter / sort
// ---------------------------------------------------------------------------

export type DocumentFilter = 'all' | 'draft' | 'review' | 'published' | 'archived';

export const documentFilterToStatus: Record<DocumentFilter, string | null> = {
  all: null,
  draft: 'DOCUMENT_STATUS_DRAFT',
  review: 'DOCUMENT_STATUS_IN_REVIEW',
  published: 'DOCUMENT_STATUS_PUBLISHED',
  archived: 'DOCUMENT_STATUS_ARCHIVED',
};

export const documentFilters: { key: DocumentFilter; labelKey: string }[] = [
  { key: 'all', labelKey: 'common.all' },
  { key: 'draft', labelKey: 'document.status.draft' },
  { key: 'review', labelKey: 'document.status.inReview' },
  { key: 'published', labelKey: 'document.status.published' },
  { key: 'archived', labelKey: 'document.status.archived' },
];

export function filterDocuments(
  documents: any[],
  search: string,
  statusFilter: DocumentFilter,
): any[] {
  let filtered = documents;

  const mappedStatus = documentFilterToStatus[statusFilter];
  if (mappedStatus) {
    filtered = filtered.filter((d: any) => getDocumentStatus(d) === mappedStatus);
  }

  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter((d: any) =>
      d.title?.toLowerCase().includes(q) ||
      d.description?.toLowerCase().includes(q) ||
      d.department?.toLowerCase().includes(q) ||
      d.tags?.some((tag: string) => tag.toLowerCase().includes(q)),
    );
  }

  return filtered;
}

export function sortDocumentsByActivity(documents: any[]): any[] {
  return [...documents].sort((a: any, b: any) => {
    const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
    const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
    return bTime - aTime;
  });
}

// ---------------------------------------------------------------------------
// Document Workspace (Wiki mode) — logic
// ---------------------------------------------------------------------------

import type {
  DocumentTreeNode,
  FlatTreeItem,
  WikiLink,
  Backlink,
  GraphNode,
  GraphEdge,
  DocumentGraph,
  PipelineStageGroup,
  ComplianceStats,
  DocumentSpace,
  RecentDocument,
} from './types';

/** Available workspace views with i18n label keys and icon identifiers. */
export const DOCUMENT_WORKSPACE_VIEWS: { id: string; labelKey: string; iconKey: string }[] = [
  { id: 'tree', labelKey: 'document.view.tree', iconKey: 'tree' },
  { id: 'grid', labelKey: 'document.view.grid', iconKey: 'grid' },
  { id: 'table', labelKey: 'document.view.table', iconKey: 'table' },
  { id: 'graph', labelKey: 'document.view.graph', iconKey: 'graph' },
];

// Folder document type constant
const FOLDER_TYPE = 'DOCUMENT_TYPE_FOLDER';

function isFolder(doc: any): boolean {
  return doc?.type === FOLDER_TYPE || doc?.is_folder === true;
}

/**
 * Transform a flat document list into a tree structure using the `parent` field.
 * Documents without a parent (or whose parent is not in the list) become root nodes.
 */
export function buildDocumentTree(docs: any[]): DocumentTreeNode[] {
  const nodeMap = new Map<string, DocumentTreeNode>();
  const roots: DocumentTreeNode[] = [];

  // Create nodes
  for (const doc of docs) {
    const id = doc?.entity?.id || doc?.id || '';
    nodeMap.set(id, {
      id,
      title: doc?.title || '',
      type: doc?.type || '',
      status: doc?.status || '',
      children: [],
      depth: 0,
      isFolder: isFolder(doc),
      parent_id: doc?.parent?.entity?.id || doc?.parent_id || undefined,
      updated_at: doc?.updated_at || undefined,
      created_by: doc?.created_by || doc?.owner?.display_name || undefined,
    });
  }

  // Build hierarchy
  for (const node of nodeMap.values()) {
    if (node.parent_id && nodeMap.has(node.parent_id)) {
      const parent = nodeMap.get(node.parent_id)!;
      parent.children.push(node);
      // Mark parent as folder if it has children
      parent.isFolder = true;
    } else {
      roots.push(node);
    }
  }

  // Assign depths recursively
  function assignDepths(nodes: DocumentTreeNode[], depth: number) {
    for (const node of nodes) {
      node.depth = depth;
      assignDepths(node.children, depth + 1);
    }
  }
  assignDepths(roots, 0);

  return roots;
}

/**
 * Flatten a tree into a list suitable for rendering, respecting expanded state.
 * Each item includes depth info for indentation.
 */
export function flattenDocumentTree(
  tree: DocumentTreeNode[],
  expandedIds: Set<string>,
  startDepth = 0,
): FlatTreeItem[] {
  const result: FlatTreeItem[] = [];

  function walk(nodes: DocumentTreeNode[], depth: number) {
    for (const node of nodes) {
      const hasChildren = node.children.length > 0;
      const expanded = expandedIds.has(node.id);
      result.push({
        id: node.id,
        title: node.title,
        type: node.type,
        status: node.status,
        depth,
        isFolder: node.isFolder,
        isExpanded: expanded,
        hasChildren,
        parent_id: node.parent_id,
        updated_at: node.updated_at,
        created_by: node.created_by,
      });
      if (hasChildren && expanded) {
        walk(node.children, depth + 1);
      }
    }
  }

  walk(tree, startDepth);
  return result;
}

/**
 * Return the ancestor chain (breadcrumbs) for a given document ID.
 * Returns an array from root to the target document (inclusive).
 */
export function getDocumentBreadcrumbs(
  docId: string,
  allDocs: any[],
): { id: string; title: string }[] {
  const byId = new Map<string, any>();
  for (const doc of allDocs) {
    const id = doc?.entity?.id || doc?.id || '';
    byId.set(id, doc);
  }

  const chain: { id: string; title: string }[] = [];
  let currentId: string | undefined = docId;
  const visited = new Set<string>();

  while (currentId && byId.has(currentId) && !visited.has(currentId)) {
    visited.add(currentId);
    const d: any = byId.get(currentId)!;
    chain.unshift({ id: currentId, title: d?.title || '' });
    currentId = d?.parent?.entity?.id || d?.parent_id || undefined;
  }

  return chain;
}

/**
 * Compute aggregate statistics for a set of documents.
 */
export function computeDocumentStats(docs: any[]): {
  total: number;
  draft: number;
  published: number;
  inReview: number;
  archived: number;
} {
  let draft = 0;
  let published = 0;
  let inReview = 0;
  let archived = 0;

  for (const doc of docs) {
    const status = doc?.status || '';
    switch (status) {
      case 'DOCUMENT_STATUS_DRAFT':
        draft++;
        break;
      case 'DOCUMENT_STATUS_PUBLISHED':
        published++;
        break;
      case 'DOCUMENT_STATUS_IN_REVIEW':
      case 'DOCUMENT_STATUS_PENDING_APPROVAL':
        inReview++;
        break;
      case 'DOCUMENT_STATUS_ARCHIVED':
        archived++;
        break;
    }
  }

  return { total: docs.length, draft, published, inReview, archived };
}

/**
 * Filter documents that belong to a specific parent folder.
 * Returns direct children only.
 */
export function filterDocumentsByFolder(docs: any[], folderId: string | undefined): any[] {
  if (!folderId) {
    // Root level: documents with no parent
    return docs.filter((d: any) => {
      const parentId = d?.parent?.entity?.id || d?.parent_id;
      return !parentId;
    });
  }
  return docs.filter((d: any) => {
    const parentId = d?.parent?.entity?.id || d?.parent_id;
    return parentId === folderId;
  });
}

/**
 * Search documents within a tree, returning matches with their parent path context.
 */
export function searchDocumentsInTree(
  tree: DocumentTreeNode[],
  query: string,
): DocumentTreeNode[] {
  if (!query.trim()) return [];
  const q = query.toLowerCase();
  const results: DocumentTreeNode[] = [];

  function search(nodes: DocumentTreeNode[]) {
    for (const node of nodes) {
      if (node.title.toLowerCase().includes(q)) {
        results.push(node);
      }
      search(node.children);
    }
  }

  search(tree);
  return results;
}

/**
 * Get the icon key for a document based on its type.
 * Returns the SVG path from documentTypeIconMap.
 */
export function getDocumentIcon(doc: any): string {
  const type = doc?.type || '';
  if (isFolder(doc)) {
    // Folder icon path
    return 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z';
  }
  return documentTypeIconMap[type] ?? documentTypeIconMap.DOCUMENT_TYPE_OTHER;
}

/**
 * Check if a user can edit the given document.
 * Owners can always edit. Drafts and revisions are editable.
 */
export function canEditDocumentByUser(doc: any, userId: string): boolean {
  if (!doc || !userId) return false;
  const ownerId = doc?.owner?.entity?.id || doc?.owner?.id || doc?.created_by_id || '';
  if (ownerId === userId) return true;

  const status = doc?.status || '';
  const editableStatuses = new Set([
    'DOCUMENT_STATUS_DRAFT',
    'DOCUMENT_STATUS_REVISION',
    'DOCUMENT_STATUS_REJECTED',
  ]);
  return editableStatuses.has(status);
}

/**
 * Check if a user can delete the given document.
 * Only owners can delete, and only certain statuses allow deletion.
 */
export function canDeleteDocumentByUser(doc: any, userId: string): boolean {
  if (!doc || !userId) return false;
  const ownerId = doc?.owner?.entity?.id || doc?.owner?.id || doc?.created_by_id || '';
  if (ownerId !== userId) return false;

  const status = doc?.status || '';
  const deletableStatuses = new Set([
    'DOCUMENT_STATUS_DRAFT',
    'DOCUMENT_STATUS_ARCHIVED',
    'DOCUMENT_STATUS_WITHDRAWN',
  ]);
  return deletableStatuses.has(status);
}

// ---------------------------------------------------------------------------
// Document Workspace (Formal mode) — logic
// ---------------------------------------------------------------------------

/** Available views for formal document mode with i18n label keys and icon identifiers. */
export const FORMAL_DOCUMENT_VIEWS: { id: string; labelKey: string; iconKey: string }[] = [
  { id: 'registry', labelKey: 'document.formalView.registry', iconKey: 'table' },
  { id: 'pipeline', labelKey: 'document.formalView.pipeline', iconKey: 'columns' },
  { id: 'review_queue', labelKey: 'document.formalView.reviewQueue', iconKey: 'queue' },
  { id: 'expiring', labelKey: 'document.formalView.expiring', iconKey: 'clock' },
  { id: 'acknowledgments', labelKey: 'document.formalView.acknowledgments', iconKey: 'check' },
];

/**
 * Filter documents that are currently in review status.
 * Optionally filter by a specific review sub-status.
 */
export function filterByReviewStatus(docs: any[], status?: string): any[] {
  const targetStatus = status || 'DOCUMENT_STATUS_IN_REVIEW';
  return docs.filter((d: any) => getDocumentStatus(d) === targetStatus);
}

/**
 * Filter documents that are expiring within the given number of days.
 * Uses `expiry_date` or `review_date` field on the document.
 */
export function filterExpiring(docs: any[], withinDays: number): any[] {
  const now = Date.now();
  const cutoff = now + withinDays * 24 * 60 * 60 * 1000;
  return docs.filter((d: any) => {
    const dateStr = d?.expiry_date || d?.review_date;
    if (!dateStr) return false;
    const ts = new Date(dateStr).getTime();
    return ts <= cutoff;
  });
}

/**
 * Calculate acknowledgment progress for a document.
 */
export function getAcknowledgmentProgress(doc: any): {
  total: number;
  acknowledged: number;
  percentage: number;
} {
  const total = doc?.acknowledgment_total ?? doc?.acknowledgments?.length ?? 0;
  const acknowledged =
    doc?.acknowledgment_count ??
    doc?.acknowledgments?.filter((a: any) => a?.status === 'acknowledged')?.length ??
    0;
  const percentage = total > 0 ? Math.round((acknowledged / total) * 100) : 0;
  return { total, acknowledged, percentage };
}

/**
 * Return the stages of the document approval pipeline.
 * Each stage has an id, i18n label key, and a corresponding document status.
 */
export function getPipelineStages(): { id: string; labelKey: string; status: string }[] {
  return [
    { id: 'draft', labelKey: 'document.pipeline.draft', status: 'DOCUMENT_STATUS_DRAFT' },
    { id: 'in_review', labelKey: 'document.pipeline.inReview', status: 'DOCUMENT_STATUS_IN_REVIEW' },
    { id: 'pending_approval', labelKey: 'document.pipeline.pendingApproval', status: 'DOCUMENT_STATUS_PENDING_APPROVAL' },
    { id: 'approved', labelKey: 'document.pipeline.approved', status: 'DOCUMENT_STATUS_APPROVED' },
    { id: 'published', labelKey: 'document.pipeline.published', status: 'DOCUMENT_STATUS_PUBLISHED' },
  ];
}

/**
 * Get the next pipeline stage after the given stage id, or null if at end.
 */
export function getNextPipelineStage(
  currentStage: string,
): { id: string; labelKey: string; status: string } | null {
  const stages = getPipelineStages();
  const idx = stages.findIndex((s) => s.id === currentStage);
  if (idx < 0 || idx >= stages.length - 1) return null;
  return stages[idx + 1];
}

/**
 * Check if a document is expiring within the specified number of days.
 */
export function isDocumentExpiringSoon(doc: any, withinDays: number): boolean {
  const dateStr = doc?.expiry_date || doc?.review_date;
  if (!dateStr) return false;
  const ts = new Date(dateStr).getTime();
  const cutoff = Date.now() + withinDays * 24 * 60 * 60 * 1000;
  return ts <= cutoff;
}

/**
 * Determine the review priority based on how long the document has been in review.
 * high: > 14 days, medium: > 7 days, low: <= 7 days.
 */
export function getReviewPriority(doc: any): 'high' | 'medium' | 'low' {
  const submittedAt = doc?.submitted_at || doc?.updated_at || doc?.created_at;
  if (!submittedAt) return 'low';
  const daysInReview = (Date.now() - new Date(submittedAt).getTime()) / (1000 * 60 * 60 * 24);
  if (daysInReview > 14) return 'high';
  if (daysInReview > 7) return 'medium';
  return 'low';
}

// ---------------------------------------------------------------------------
// Pipeline — kanban stage grouping
// ---------------------------------------------------------------------------

/**
 * Group documents by pipeline stage for kanban-style rendering.
 * Returns one group per stage, with documents sorted by updated_at.
 */
export function groupDocumentsByPipelineStage(docs: any[]): PipelineStageGroup[] {
  const stages = getPipelineStages();
  const groups: PipelineStageGroup[] = stages.map((stage) => ({
    stageId: stage.id,
    labelKey: stage.labelKey,
    status: stage.status,
    documents: [],
    count: 0,
  }));

  const statusToGroup = new Map<string, PipelineStageGroup>();
  for (const group of groups) {
    statusToGroup.set(group.status, group);
  }

  for (const doc of docs) {
    const status = getDocumentStatus(doc);
    const group = statusToGroup.get(status);
    if (group) {
      group.documents.push(doc);
      group.count++;
    }
  }

  // Sort each group by updated_at descending
  for (const group of groups) {
    group.documents.sort((a: any, b: any) => {
      const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
      return bTime - aTime;
    });
  }

  return groups;
}

// ---------------------------------------------------------------------------
// Compliance stats
// ---------------------------------------------------------------------------

/**
 * Compute compliance-oriented statistics for a set of documents.
 * Useful for formal/QMS dashboards.
 */
export function computeComplianceStats(docs: any[]): ComplianceStats {
  const stats: ComplianceStats = {
    total: docs.length,
    published: 0,
    expiringSoon: 0,
    overdue: 0,
    pendingAcknowledgment: 0,
    acknowledgedPercent: 0,
    byType: {},
    byConfidentiality: {},
  };

  let totalAckRequired = 0;
  let totalAckDone = 0;

  for (const doc of docs) {
    const status = getDocumentStatus(doc);
    const type = getDocumentType(doc);
    const conf = getDocumentConfidentiality(doc);

    if (status === 'DOCUMENT_STATUS_PUBLISHED') stats.published++;

    if (isDocumentExpiringSoon(doc, 30)) stats.expiringSoon++;

    // Overdue: past expiry_date and still published
    const expiryStr = doc?.expiry_date || doc?.review_date;
    if (expiryStr && new Date(expiryStr).getTime() < Date.now() && status === 'DOCUMENT_STATUS_PUBLISHED') {
      stats.overdue++;
    }

    // Acknowledgment tracking
    const ackProgress = getAcknowledgmentProgress(doc);
    if (ackProgress.total > 0) {
      totalAckRequired += ackProgress.total;
      totalAckDone += ackProgress.acknowledged;
      if (ackProgress.acknowledged < ackProgress.total) {
        stats.pendingAcknowledgment++;
      }
    }

    // Count by type
    if (type) stats.byType[type] = (stats.byType[type] || 0) + 1;
    if (conf) stats.byConfidentiality[conf] = (stats.byConfidentiality[conf] || 0) + 1;
  }

  stats.acknowledgedPercent = totalAckRequired > 0
    ? Math.round((totalAckDone / totalAckRequired) * 100)
    : 100;

  return stats;
}

// ---------------------------------------------------------------------------
// Zettelkasten / Wiki-links — parsing and graph building
// ---------------------------------------------------------------------------

/** Regex for [[wikilinks]] — supports [[Target]] and [[id|Display Text]] */
const WIKILINK_RE = /\[\[([^\]]+)\]\]/g;

/**
 * Parse wikilinks from a block's rich_text content.
 * Returns array of WikiLink objects with raw text and display text.
 */
export function parseWikiLinks(richText: any[], blockId: string): WikiLink[] {
  const links: WikiLink[] = [];
  for (const segment of richText) {
    const text = segment?.text?.content || segment?.plain_text || '';
    let match: RegExpExecArray | null;
    WIKILINK_RE.lastIndex = 0;
    while ((match = WIKILINK_RE.exec(text)) !== null) {
      const raw = match[1];
      const pipeIndex = raw.indexOf('|');
      links.push({
        raw,
        displayText: pipeIndex >= 0 ? raw.slice(pipeIndex + 1).trim() : raw.trim(),
        blockId,
      });
    }
  }
  return links;
}

/**
 * Extract all wikilinks from a document's blocks.
 */
export function extractWikiLinksFromBlocks(blocks: any[]): WikiLink[] {
  const allLinks: WikiLink[] = [];
  for (const block of blocks) {
    const richText = block?.rich_text || block?.paragraph?.rich_text || [];
    const blockId = block?.entity?.id || block?.id || '';
    allLinks.push(...parseWikiLinks(richText, blockId));
  }
  return allLinks;
}

/**
 * Resolve wikilink targets by matching raw text against document titles or IDs.
 * Mutates the input array by setting targetId on matches.
 */
export function resolveWikiLinks(
  links: WikiLink[],
  allDocs: any[],
): WikiLink[] {
  const titleMap = new Map<string, string>(); // lowercase title → id
  const idSet = new Set<string>();
  for (const doc of allDocs) {
    const id = getEntityId(doc);
    const title = getDocumentTitle(doc).toLowerCase();
    idSet.add(id);
    if (title) titleMap.set(title, id);
  }

  for (const link of links) {
    const rawLower = link.raw.toLowerCase();
    const pipeIndex = rawLower.indexOf('|');
    const target = pipeIndex >= 0 ? rawLower.slice(0, pipeIndex).trim() : rawLower;

    // Try matching by title first, then by ID
    if (titleMap.has(target)) {
      link.targetId = titleMap.get(target);
    } else if (idSet.has(target)) {
      link.targetId = target;
    }
  }

  return links;
}

/**
 * Build backlinks for a given document ID by scanning all documents' blocks.
 * Returns a list of documents that reference the target via wikilinks.
 */
export function buildBacklinks(
  targetDocId: string,
  targetTitle: string,
  allDocs: any[],
  allBlocksByDocId: Map<string, any[]>,
): Backlink[] {
  const backlinks: Backlink[] = [];
  const targetTitleLower = targetTitle.toLowerCase();

  for (const doc of allDocs) {
    const sourceId = getEntityId(doc);
    if (sourceId === targetDocId) continue;

    const blocks = allBlocksByDocId.get(sourceId) || [];
    for (const block of blocks) {
      const richText = block?.rich_text || block?.paragraph?.rich_text || [];
      const textContent = richText.map((s: any) => s?.text?.content || s?.plain_text || '').join('');

      // Check for [[targetTitle]] or [[targetDocId]] reference
      const matches = textContent.match(WIKILINK_RE);
      if (!matches) continue;

      for (const match of matches) {
        const inner = match.slice(2, -2);
        const pipeIndex = inner.indexOf('|');
        const ref = (pipeIndex >= 0 ? inner.slice(0, pipeIndex) : inner).trim().toLowerCase();

        if (ref === targetTitleLower || ref === targetDocId) {
          // Extract surrounding text as snippet (up to 120 chars)
          const snippetStart = Math.max(0, textContent.indexOf(match) - 40);
          const snippetEnd = Math.min(textContent.length, textContent.indexOf(match) + match.length + 40);
          const snippet = (snippetStart > 0 ? '...' : '') +
            textContent.slice(snippetStart, snippetEnd) +
            (snippetEnd < textContent.length ? '...' : '');

          backlinks.push({
            sourceId,
            sourceTitle: getDocumentTitle(doc),
            sourceIcon: doc?.icon,
            sourceStatus: getDocumentStatus(doc),
            snippet,
            updatedAt: doc?.updated_at,
          });
          break; // One backlink per source document
        }
      }
    }
  }

  return backlinks;
}

/**
 * Build a knowledge graph from documents and their wikilink relationships.
 * Includes parent-child edges and resource link edges.
 */
export function buildDocumentGraph(
  docs: any[],
  allBlocksByDocId: Map<string, any[]>,
  focusDocId?: string,
): DocumentGraph {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const connectionCount = new Map<string, number>();

  // Initialize nodes
  for (const doc of docs) {
    const id = getEntityId(doc);
    connectionCount.set(id, 0);
    nodes.push({
      id,
      title: getDocumentTitle(doc),
      type: getDocumentType(doc),
      status: getDocumentStatus(doc),
      connections: 0,
      isFocus: id === focusDocId,
    });
  }

  const nodeIdSet = new Set(nodes.map((n) => n.id));

  // Parent-child edges
  for (const doc of docs) {
    const id = getEntityId(doc);
    const parentId = doc?.parent?.entity?.id || doc?.parent_id;
    if (parentId && nodeIdSet.has(parentId)) {
      edges.push({ sourceId: parentId, targetId: id, relation: 'parent' });
      connectionCount.set(parentId, (connectionCount.get(parentId) || 0) + 1);
      connectionCount.set(id, (connectionCount.get(id) || 0) + 1);
    }
  }

  // Wikilink edges
  const titleToId = new Map<string, string>();
  for (const doc of docs) {
    titleToId.set(getDocumentTitle(doc).toLowerCase(), getEntityId(doc));
  }

  for (const doc of docs) {
    const sourceId = getEntityId(doc);
    const blocks = allBlocksByDocId.get(sourceId) || [];
    const links = extractWikiLinksFromBlocks(blocks);
    const resolved = resolveWikiLinks(links, docs);
    const seenTargets = new Set<string>();

    for (const link of resolved) {
      if (link.targetId && link.targetId !== sourceId && !seenTargets.has(link.targetId)) {
        seenTargets.add(link.targetId);
        edges.push({ sourceId, targetId: link.targetId, relation: 'wikilink' });
        connectionCount.set(sourceId, (connectionCount.get(sourceId) || 0) + 1);
        connectionCount.set(link.targetId, (connectionCount.get(link.targetId) || 0) + 1);
      }
    }
  }

  // Update connection counts on nodes
  for (const node of nodes) {
    node.connections = connectionCount.get(node.id) || 0;
  }

  return { nodes, edges };
}

// ---------------------------------------------------------------------------
// Spaces — logical document collections
// ---------------------------------------------------------------------------

/** Default spaces for organizing documents */
export const DEFAULT_DOCUMENT_SPACES: DocumentSpace[] = [
  {
    id: 'all',
    title: 'document.space.all',
    iconKey: 'folder',
    rootIds: [],
    docCount: 0,
    isDefault: true,
  },
  {
    id: 'wiki',
    title: 'document.space.wiki',
    iconKey: 'book-open',
    description: 'document.space.wikiDesc',
    rootIds: [],
    docCount: 0,
  },
  {
    id: 'policies',
    title: 'document.space.policies',
    iconKey: 'shield',
    description: 'document.space.policiesDesc',
    rootIds: [],
    docCount: 0,
  },
  {
    id: 'procedures',
    title: 'document.space.procedures',
    iconKey: 'clipboard',
    description: 'document.space.proceduresDesc',
    rootIds: [],
    docCount: 0,
  },
];

/** QMS document types — subset relevant for quality management */
/** Document types that are inherently formal/controlled (QMS, compliance). */
export const QMS_TYPES = new Set([
  'DOCUMENT_TYPE_POLICY',
  'DOCUMENT_TYPE_MANUAL',
  'DOCUMENT_TYPE_PROCEDURE',
  'DOCUMENT_TYPE_WORK_INSTRUCTION',
  'DOCUMENT_TYPE_FORM',
  'DOCUMENT_TYPE_RECORD',
  'DOCUMENT_TYPE_SPECIFICATION',
  'DOCUMENT_TYPE_CONTRACT',
]);

/**
 * Determine whether a document should be rendered in formal/controlled mode.
 * Uses the `controlled` field from the API when available;
 * otherwise infers from document type (QMS types are always controlled)
 * or from the presence of page_settings.
 */
export function isControlledDocument(doc: any): boolean {
  // Explicit flag from backend
  if (doc?.controlled === true) return true;
  // Has page settings (A4 layout, headers/footers) → formal
  if (doc?.page_settings?.paper_size) return true;
  // QMS document type → formal
  const type = getDocumentType(doc);
  if (type && QMS_TYPES.has(type)) return true;
  return false;
}

/**
 * Classify documents into spaces by their type.
 * Returns updated spaces with correct docCounts and rootIds.
 */
export function classifyDocumentsIntoSpaces(
  docs: any[],
  spaces: DocumentSpace[],
): DocumentSpace[] {
  const result = spaces.map((s) => ({ ...s, docCount: 0, rootIds: [] as string[] }));
  const spaceMap = new Map(result.map((s) => [s.id, s]));
  const allSpace = spaceMap.get('all');

  for (const doc of docs) {
    const id = getEntityId(doc);
    const type = getDocumentType(doc);

    // All space always gets everything
    if (allSpace) {
      allSpace.docCount++;
    }

    // Wiki: non-controlled, page-style docs
    if (!doc?.controlled && !QMS_TYPES.has(type)) {
      const wiki = spaceMap.get('wiki');
      if (wiki) {
        wiki.docCount++;
        if (!doc?.parent?.entity?.id && !doc?.parent_id) wiki.rootIds.push(id);
      }
    }

    // Policies
    if (type === 'DOCUMENT_TYPE_POLICY' || type === 'DOCUMENT_TYPE_MANUAL') {
      const policies = spaceMap.get('policies');
      if (policies) {
        policies.docCount++;
        if (!doc?.parent?.entity?.id && !doc?.parent_id) policies.rootIds.push(id);
      }
    }

    // Procedures
    if (type === 'DOCUMENT_TYPE_PROCEDURE' || type === 'DOCUMENT_TYPE_WORK_INSTRUCTION') {
      const procedures = spaceMap.get('procedures');
      if (procedures) {
        procedures.docCount++;
        if (!doc?.parent?.entity?.id && !doc?.parent_id) procedures.rootIds.push(id);
      }
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Recent documents
// ---------------------------------------------------------------------------

const RECENT_DOCS_KEY = 'corde_recent_docs';
const MAX_RECENT_DOCS = 20;

/**
 * Get recently accessed documents from localStorage.
 */
export function getRecentDocuments(): RecentDocument[] {
  try {
    const raw = localStorage.getItem(RECENT_DOCS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as RecentDocument[];
  } catch {
    return [];
  }
}

/**
 * Track a document access. Adds to front of recent list, deduplicates, caps at MAX.
 */
export function trackDocumentAccess(doc: any): void {
  const id = getEntityId(doc);
  if (!id) return;

  const entry: RecentDocument = {
    id,
    title: getDocumentTitle(doc),
    type: getDocumentType(doc),
    status: getDocumentStatus(doc),
    icon: doc?.icon,
    accessedAt: new Date().toISOString(),
  };

  const existing = getRecentDocuments().filter((r) => r.id !== id);
  const updated = [entry, ...existing].slice(0, MAX_RECENT_DOCS);

  try {
    localStorage.setItem(RECENT_DOCS_KEY, JSON.stringify(updated));
  } catch {
    // Storage full — silently ignore
  }
}

// ---------------------------------------------------------------------------
// Favorites
// ---------------------------------------------------------------------------

const FAVORITES_KEY = 'corde_favorite_docs';

/**
 * Get favorite document IDs from localStorage.
 */
export function getFavoriteDocIds(): Set<string> {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

/**
 * Toggle a document's favorite status. Returns the new set.
 */
export function toggleFavoriteDoc(docId: string): Set<string> {
  const favs = getFavoriteDocIds();
  if (favs.has(docId)) {
    favs.delete(docId);
  } else {
    favs.add(docId);
  }
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify([...favs]));
  } catch {
    // Storage full
  }
  return favs;
}

/**
 * Filter documents to only favorites.
 */
export function filterFavorites(docs: any[], favIds: Set<string>): any[] {
  return docs.filter((d) => favIds.has(getEntityId(d)));
}

// ---------------------------------------------------------------------------
// Daily notes (Zettelkasten)
// ---------------------------------------------------------------------------

/**
 * Generate a daily note title for a given date (YYYY-MM-DD format).
 */
export function getDailyNoteTitle(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Find an existing daily note for a given date, or return null.
 */
export function findDailyNote(docs: any[], date: Date): any | null {
  const title = getDailyNoteTitle(date);
  return docs.find((d) => getDocumentTitle(d) === title) || null;
}

/**
 * Suggest wikilink targets matching a query prefix.
 * Used for [[autocomplete when typing.
 */
export function suggestWikiLinkTargets(
  query: string,
  allDocs: any[],
  excludeDocId?: string,
  limit = 8,
): { id: string; title: string; type: string }[] {
  if (!query.trim()) return [];
  const q = query.toLowerCase();
  const results: { id: string; title: string; type: string }[] = [];

  for (const doc of allDocs) {
    const id = getEntityId(doc);
    if (id === excludeDocId) continue;
    const title = getDocumentTitle(doc);
    if (title.toLowerCase().includes(q)) {
      results.push({ id, title, type: getDocumentType(doc) });
      if (results.length >= limit) break;
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// Document number formatting
// ---------------------------------------------------------------------------

/**
 * Format a document number for display in registry views.
 * Falls back to showing the document type prefix if no number assigned.
 */
export function formatDocumentNumber(doc: any): string {
  if (doc?.document_number) return doc.document_number;
  const aspect = doc?.document_aspect;
  if (aspect?.document_number) return aspect.document_number;
  return '';
}

/**
 * Get the effective date of a document (from aspect or direct field).
 */
export function getEffectiveDate(doc: any): string | undefined {
  return doc?.effective_date || doc?.document_aspect?.effective_date;
}

/**
 * Get the next review date of a document.
 */
export function getNextReviewDate(doc: any): string | undefined {
  return doc?.review_date || doc?.document_aspect?.next_review_date;
}

/**
 * Get the current version label of a document.
 */
export function getDocumentVersion(doc: any): string {
  return doc?.version || doc?.document_aspect?.version || doc?.current_version || '';
}

// ---------------------------------------------------------------------------
// Page layout helpers (for paged/Word-style rendering)
// ---------------------------------------------------------------------------

/** Paper size dimensions in mm */
export const PAPER_DIMENSIONS: Record<string, { width: number; height: number }> = {
  PAPER_SIZE_A4: { width: 210, height: 297 },
  PAPER_SIZE_A3: { width: 297, height: 420 },
  PAPER_SIZE_LETTER: { width: 216, height: 279 },
  PAPER_SIZE_LEGAL: { width: 216, height: 356 },
};

/** Default margins in mm (GOST R 7.0.97-2016 for Russian formal docs) */
export const DEFAULT_MARGINS = { top_mm: 20, bottom_mm: 20, left_mm: 30, right_mm: 15 };

/** Default page settings for controlled documents */
export const DEFAULT_PAGE_SETTINGS = {
  paper_size: 'PAPER_SIZE_A4' as const,
  orientation: 'PAGE_ORIENTATION_PORTRAIT' as const,
  margins: DEFAULT_MARGINS,
  page_numbering_start: 1,
  first_page_different: false,
};

/**
 * Get resolved page dimensions in pixels at 96 DPI.
 * Applies orientation (swap width/height for landscape).
 */
export function getPageDimensionsPx(doc: any): { widthPx: number; heightPx: number; marginsPx: { top: number; bottom: number; left: number; right: number } } {
  const settings = doc?.page_settings || DEFAULT_PAGE_SETTINGS;
  const paperSize = settings.paper_size || 'PAPER_SIZE_A4';
  const orientation = settings.orientation || 'PAGE_ORIENTATION_PORTRAIT';
  const margins = settings.margins || DEFAULT_MARGINS;

  const dim = PAPER_DIMENSIONS[paperSize] || PAPER_DIMENSIONS.PAPER_SIZE_A4;
  const isLandscape = orientation === 'PAGE_ORIENTATION_LANDSCAPE';
  const mmToPx = 96 / 25.4; // 1 inch = 25.4mm, 96 DPI

  return {
    widthPx: Math.round((isLandscape ? dim.height : dim.width) * mmToPx),
    heightPx: Math.round((isLandscape ? dim.width : dim.height) * mmToPx),
    marginsPx: {
      top: Math.round(margins.top_mm * mmToPx),
      bottom: Math.round(margins.bottom_mm * mmToPx),
      left: Math.round(margins.left_mm * mmToPx),
      right: Math.round(margins.right_mm * mmToPx),
    },
  };
}

/**
 * Get the content area height per page (total height minus top/bottom margins and header/footer space).
 */
export function getContentHeightPx(doc: any): number {
  const { heightPx, marginsPx } = getPageDimensionsPx(doc);
  const headerFooterSpace = 40; // ~15mm for header + footer combined
  return heightPx - marginsPx.top - marginsPx.bottom - headerFooterSpace;
}

// ---------------------------------------------------------------------------
// Header / Footer variables
// ---------------------------------------------------------------------------

import type { PolicyFormData, HeaderFooterContext } from './types';

/** Available template variables for headers and footers */
export const HEADER_FOOTER_VARIABLES = [
  { key: '{document_number}', labelKey: 'headerFooter.var.documentNumber', example: 'ORD-2026-001' },
  { key: '{page}', labelKey: 'headerFooter.var.page', example: '1' },
  { key: '{total_pages}', labelKey: 'headerFooter.var.totalPages', example: '5' },
  { key: '{title}', labelKey: 'headerFooter.var.title', example: 'Safety Manual' },
  { key: '{version}', labelKey: 'headerFooter.var.version', example: 'v2.1' },
  { key: '{date}', labelKey: 'headerFooter.var.date', example: '2026-03-15' },
  { key: '{department}', labelKey: 'headerFooter.var.department', example: 'Engineering' },
] as const;

/** Resolve template variables in header/footer text */
export function resolveHeaderFooterText(
  template: string,
  ctx: HeaderFooterContext,
): string {
  return template
    .replace(/\{document_number\}/g, ctx.document_number || '')
    .replace(/\{page\}/g, ctx.page != null ? String(ctx.page) : '')
    .replace(/\{total_pages\}/g, ctx.total_pages != null ? String(ctx.total_pages) : '')
    .replace(/\{title\}/g, ctx.title || '')
    .replace(/\{version\}/g, ctx.version || '')
    .replace(/\{date\}/g, ctx.date || '')
    .replace(/\{department\}/g, ctx.department || '');
}

/** Build header and footer objects from page settings, resolving variables */
export function buildHeaderFooterFromSettings(
  settings: any,
  ctx: HeaderFooterContext,
): { header: { left: string; center: string; right: string }; footer: { left: string; center: string; right: string } } {
  const h = settings?.header || {};
  const f = settings?.footer || {};

  return {
    header: {
      left: resolveHeaderFooterText(h.left || '', ctx),
      center: resolveHeaderFooterText(h.center || '', ctx),
      right: resolveHeaderFooterText(h.right || '', ctx),
    },
    footer: {
      left: resolveHeaderFooterText(f.left || '', ctx),
      center: resolveHeaderFooterText(f.center || '{page}', ctx),
      right: resolveHeaderFooterText(f.right || '', ctx),
    },
  };
}

// ---------------------------------------------------------------------------
// Document Policy form helpers
// ---------------------------------------------------------------------------

/** Get default form data, optionally pre-filled from an existing policy */
export function getDefaultPolicyFormData(policy?: any): PolicyFormData {
  return {
    inherit_from_parent: policy?.inherit_from_parent ?? true,
    allowed_document_type_ids: policy?.allowed_document_type_ids || [],
    default_document_type_id: policy?.default_document_type_id || '',
    numbering_scheme_id: policy?.numbering_scheme_id || '',
    numbering_prefix: policy?.numbering_prefix || '',
    require_approval: policy?.require_approval ?? false,
    default_approval_pipeline_id: policy?.default_approval_pipeline_id || '',
    require_acknowledgment: policy?.require_acknowledgment ?? false,
    enable_versioning: policy?.enable_versioning ?? false,
    enable_review_cycle: policy?.enable_review_cycle ?? false,
    default_review_period_days: policy?.default_review_period_days || 365,
    default_confidentiality: policy?.default_confidentiality || '',
    enable_registration: policy?.enable_registration ?? false,
    registration_prefix: policy?.registration_prefix || '',
  };
}

/** Validate policy form data, returns array of error messages */
export function validatePolicyForm(form: PolicyFormData): string[] {
  const errors: string[] = [];

  if (form.enable_review_cycle && form.default_review_period_days <= 0) {
    errors.push('Review period must be greater than 0 days');
  }

  if (form.enable_registration && !form.registration_prefix.trim()) {
    errors.push('Registration prefix is required when registration is enabled');
  }

  return errors;
}

/** Build API request payload from form data */
export function buildPolicyRequest(
  form: PolicyFormData,
  entityType: string,
  entityId: string,
): any {
  return {
    scope_entity_type: entityType,
    scope_entity_id: entityId,
    inherit_from_parent: form.inherit_from_parent,
    allowed_document_type_ids: form.allowed_document_type_ids,
    default_document_type_id: form.default_document_type_id || undefined,
    numbering_scheme_id: form.numbering_scheme_id || undefined,
    numbering_prefix: form.numbering_prefix || undefined,
    require_approval: form.require_approval,
    default_approval_pipeline_id: form.require_approval
      ? form.default_approval_pipeline_id || undefined
      : undefined,
    require_acknowledgment: form.require_acknowledgment,
    enable_versioning: form.enable_versioning,
    enable_review_cycle: form.enable_review_cycle,
    default_review_period_days: form.enable_review_cycle
      ? form.default_review_period_days
      : undefined,
    default_confidentiality: form.default_confidentiality || undefined,
    enable_registration: form.enable_registration,
    registration_prefix: form.enable_registration
      ? form.registration_prefix
      : undefined,
  };
}

/** Extract a summary from a document policy for display */
export function extractPolicySummary(policy: any): {
  numberingPattern: string;
  approvalRequired: boolean;
  versioningEnabled: boolean;
  typesCount: number;
} {
  return {
    numberingPattern: policy?.numbering_prefix
      ? `${policy.numbering_prefix}-{seq}`
      : '',
    approvalRequired: !!policy?.require_approval,
    versioningEnabled: !!policy?.enable_versioning,
    typesCount: (policy?.allowed_document_type_ids || []).length,
  };
}

// ---------------------------------------------------------------------------
// Scope / context helpers
// ---------------------------------------------------------------------------

/** Build scope chain from entity type and parent info */
export function buildScopeChain(
  entityType: string,
  entityId: string,
  parentType?: string,
  parentId?: string,
): Array<{ type: string; id: string }> {
  const chain: Array<{ type: string; id: string }> = [
    { type: entityType, id: entityId },
  ];
  if (parentType && parentId) {
    chain.push({ type: parentType, id: parentId });
  }
  return chain;
}

/** Extract default values from a resolved document context */
export function extractPolicyDefaults(context: any): {
  type: string;
  confidentiality: string;
  department: string;
} {
  const policy = context?.effective_policy || context;
  return {
    type: policy?.default_document_type_id || '',
    confidentiality: policy?.default_confidentiality || '',
    department: '',
  };
}

/** Map a resolved context to creation form options */
export function mapContextToOptions(context: any): {
  allowedTypeIds: string[];
  defaultTypeId: string;
  requireApproval: boolean;
  defaultConfidentiality: string;
} {
  const policy = context?.effective_policy || context;
  return {
    allowedTypeIds: policy?.allowed_document_type_ids || [],
    defaultTypeId: policy?.default_document_type_id || '',
    requireApproval: !!policy?.require_approval,
    defaultConfidentiality: policy?.default_confidentiality || '',
  };
}
